import { useEffect, useCallback } from "react";
import { useWindowId } from "./OsWindow";
import { useOsActionsOptional, useOsWindowVersionOptional, useOsDockTooltipsOptional } from "./OsContext";

/**
 * Configuration for opening a new window.
 */
export interface OpenWindowConfig {
  appId: string;
  title: string;
  params?: Record<string, string>;
  initialSize?: { width: number; height: number };
  /** When false, open or reuse without raising z-index. Defaults to true. */
  focus?: boolean;
  /** When true, open or reuse the window maximized. */
  maximize?: boolean;
  /** Minimize and restore another window in the same transaction (e.g. swap from fullscreen). */
  demoteWindowId?: string;
  /** Place the opened window one layer below this window (2nd highest). Keeps anchor on top. */
  stackBelowWindowId?: string;
  /** Skip reuse and always open a new window (e.g. “Open in new window”). */
  newWindow?: boolean;
  /**
   * When reusing a window, replace params instead of subset matching.
   * Use for URL deep links and browser back/forward so the URL stays exact.
   */
  replaceParams?: boolean;
  /** Position floating sub-windows relative to this parent window when supported by the app. */
  anchorWindowId?: string;
}

// =============================================================================
// Title Hooks
// =============================================================================

/**
 * Hook to set the window title declaratively.
 * Updates the title whenever the provided value changes.
 * Safe to use outside of OS mode (no-op).
 *
 * @param title - The title to display in the window chrome
 *
 * @example
 * function DocumentEditor({ documentId }) {
 *   const [doc, setDoc] = useState(null);
 *   // Title updates automatically when doc loads
 *   useWindowTitle(doc?.name || "Loading...");
 * }
 */
export function useWindowTitle(title: string | undefined): void {
  const windowId = useWindowId();
  const actions = useOsActionsOptional();

  useEffect(() => {
    if (!windowId || !actions || !title) return;
    actions.updateWindow(windowId, { title });
  }, [windowId, actions, title]);
}

/**
 * Hook to get an imperative function for setting the window title.
 * Useful when the title changes based on async operations or user actions.
 * Safe to use outside of OS mode (returns no-op function).
 *
 * @returns Function to set the window title
 *
 * @example
 * function ReportBuilder() {
 *   const setWindowTitle = useSetWindowTitle();
 *
 *   const handleSave = async (name: string) => {
 *     await saveReport(name);
 *     setWindowTitle(name); // Update after save
 *   };
 * }
 */
export function useSetWindowTitle(): (title: string) => void {
  const windowId = useWindowId();
  const actions = useOsActionsOptional();

  return useCallback(
    (title: string) => {
      if (!windowId || !actions) return;
      actions.updateWindow(windowId, { title });
    },
    [windowId, actions],
  );
}

/**
 * Hook to get an imperative function for updating window params.
 * Merges new params with existing ones. Use to keep share URLs up-to-date
 * when the content changes (e.g., loading a different report).
 * Safe to use outside of OS mode (returns no-op function).
 *
 * @returns Function to update window params
 *
 * @example
 * function ReportBuilder() {
 *   const setWindowParams = useSetWindowParams();
 *
 *   const handleLoadReport = async (reportId: string) => {
 *     await loadReport(reportId);
 *     setWindowParams({ report_id: reportId }); // Update for share URL
 *   };
 * }
 */
export function useSetWindowParams(): (
  params: Record<string, string | undefined>,
) => void {
  const windowId = useWindowId();
  const actions = useOsActionsOptional();
  const getWindow = useOsWindowVersionOptional()?.getWindow;

  return useCallback(
    (newParams: Record<string, string | undefined>) => {
      if (!windowId || !actions) return;
      const currentWindow = getWindow?.(windowId);
      if (!currentWindow) return;

      // Merge new params with existing, removing undefined values
      const mergedParams = { ...currentWindow.params };
      Object.entries(newParams).forEach(([key, value]) => {
        if (value === undefined) {
          delete mergedParams[key];
        } else {
          mergedParams[key] = value;
        }
      });

      actions.updateWindow(windowId, { params: mergedParams });
    },
    [windowId, actions, getWindow],
  );
}

// =============================================================================
// Window Action Hooks
// =============================================================================

/**
 * Hook to get a function that closes the current window.
 * Safe to use outside of OS mode (returns no-op function).
 *
 * @returns Function to close the current window
 *
 * @example
 * function MyApp() {
 *   const closeWindow = useCloseWindow();
 *   const isOsMode = useIsOsMode();
 *
 *   const handleExit = () => {
 *     if (isOsMode) {
 *       closeWindow();
 *     } else {
 *       navigate("/home");
 *     }
 *   };
 *
 *   return <button onClick={handleExit}>Exit</button>;
 * }
 */
export function useCloseWindow(): () => void {
  const windowId = useWindowId();
  const actions = useOsActionsOptional();

  return useCallback(() => {
    if (!windowId || !actions) return;
    actions.closeWindow(windowId);
  }, [windowId, actions]);
}

/**
 * Register a guard that runs before the current OS window closes.
 * Return false to veto the close (e.g. show an unsaved-changes dialog).
 * Safe to use outside of OS mode (no-op).
 */
export function useWindowCloseGuard(guard: () => boolean): void {
  const windowId = useWindowId();
  const actions = useOsActionsOptional();

  useEffect(() => {
    if (!windowId || !actions) return;
    return actions.registerWindowCloseGuard(windowId, guard);
  }, [windowId, actions, guard]);
}

/**
 * Hook to get a function that minimizes the current window.
 * Safe to use outside of OS mode (returns no-op function).
 *
 * @returns Function to minimize the current window
 *
 * @example
 * function MyApp() {
 *   const minimizeWindow = useMinimizeWindow();
 *
 *   return <button onClick={minimizeWindow}>Minimize</button>;
 * }
 */
export function useMinimizeWindow(): () => void {
  const windowId = useWindowId();
  const actions = useOsActionsOptional();

  return useCallback(() => {
    if (!windowId || !actions) return;
    actions.updateWindow(windowId, { isMinimized: true });
  }, [windowId, actions]);
}

/**
 * Hook to get a function that toggles or sets the maximize state of the current window.
 * Safe to use outside of OS mode (returns no-op function).
 *
 * @returns Function to toggle/set maximize state. Pass boolean to set explicitly, or call without args to toggle.
 *
 * @example
 * function MyApp() {
 *   const maximizeWindow = useMaximizeWindow();
 *
 *   return (
 *     <>
 *       <button onClick={() => maximizeWindow()}>Toggle Maximize</button>
 *       <button onClick={() => maximizeWindow(true)}>Maximize</button>
 *       <button onClick={() => maximizeWindow(false)}>Restore</button>
 *     </>
 *   );
 * }
 */
export function useMaximizeWindow(): (maximized?: boolean) => void {
  const windowId = useWindowId();
  const actions = useOsActionsOptional();
  const getWindow = useOsWindowVersionOptional()?.getWindow;

  return useCallback(
    (maximized?: boolean) => {
      if (!windowId || !actions) return;
      const window = getWindow?.(windowId);
      const newState = maximized ?? !window?.isMaximized;
      actions.updateWindow(windowId, { isMaximized: newState });
    },
    [windowId, actions, getWindow],
  );
}

/**
 * Hook to get a function that opens a new window.
 * Safe to use outside of OS mode (returns no-op function).
 *
 * @returns Function to open a new window with the given configuration
 *
 * @example
 * function MyApp() {
 *   const openWindow = useOpenWindow();
 *
 *   const handleOpenReport = (reportId: string) => {
 *     openWindow({
 *       appId: "report-builder",
 *       title: "Report Builder",
 *       params: { id: reportId },
 *     });
 *   };
 *
 *   return <button onClick={() => handleOpenReport("123")}>Open Report</button>;
 * }
 */
export function useOpenWindow(): (config: OpenWindowConfig) => void {
  const actions = useOsActionsOptional();

  return useCallback(
    (config: OpenWindowConfig) => {
      if (!actions) return;
      actions.openWindow(config);
    },
    [actions],
  );
}

// =============================================================================
// Dock Tooltip Hooks
// =============================================================================

/**
 * Hook to set the dock tooltip for an app declaratively.
 * The tooltip is automatically cleared when the component unmounts.
 * Safe to use outside of OS mode (no-op).
 *
 * @param appId - The app ID to set the tooltip for
 * @param tooltip - The tooltip text to display (or undefined to clear)
 *
 * @example
 * function PodInstance({ templateName }) {
 *   // Shows "Daily IHF Reconciliations" when hovering the pods dock icon
 *   useDockTooltip("pods", templateName);
 * }
 */
export function useDockTooltip(appId: string, tooltip: string | undefined): void {
  const dockTooltips = useOsDockTooltipsOptional();

  useEffect(() => {
    if (!dockTooltips || !tooltip) return;
    dockTooltips.setTooltip(appId, tooltip);
    return () => {
      dockTooltips.setTooltip(appId, null);
    };
  }, [dockTooltips, appId, tooltip]);
}

/**
 * Hook to get an imperative function for setting dock tooltips.
 * Useful when the tooltip needs to be set/cleared dynamically.
 * Safe to use outside of OS mode (returns no-op function).
 *
 * @returns Object with setTooltip and clearTooltip functions
 *
 * @example
 * function MissionControl() {
 *   const { setTooltip, clearTooltip } = useSetDockTooltip();
 *
 *   const handleFocus = (appId: string, name: string) => {
 *     setTooltip(appId, name);
 *   };
 *
 *   const handleBlur = (appId: string) => {
 *     clearTooltip(appId);
 *   };
 * }
 */
export function useSetDockTooltip(): {
  setTooltip: (appId: string, tooltip: string) => void;
  clearTooltip: (appId: string) => void;
} {
  const dockTooltips = useOsDockTooltipsOptional();

  const setTooltip = useCallback(
    (appId: string, tooltip: string) => {
      if (!dockTooltips) return;
      dockTooltips.setTooltip(appId, tooltip);
    },
    [dockTooltips],
  );

  const clearTooltip = useCallback(
    (appId: string) => {
      if (!dockTooltips) return;
      dockTooltips.setTooltip(appId, null);
    },
    [dockTooltips],
  );

  return { setTooltip, clearTooltip };
}

// =============================================================================
// Convenience Hook
// =============================================================================

/**
 * Hook that returns all window actions in a single object.
 * Useful for modules that need multiple window operations.
 * All actions are safe to use outside of OS mode (no-op functions).
 *
 * @returns Object containing all window action functions
 *
 * @example
 * function MyApp() {
 *   const { close, minimize, maximize, openWindow, setTitle, setParams } = useWindowActions();
 *
 *   return (
 *     <div>
 *       <button onClick={close}>Close</button>
 *       <button onClick={minimize}>Minimize</button>
 *       <button onClick={() => maximize()}>Toggle Maximize</button>
 *       <button onClick={() => setTitle("New Title")}>Set Title</button>
 *       <button onClick={() => setParams({ report_id: "123" })}>Set Params</button>
 *     </div>
 *   );
 * }
 */
export function useWindowActions() {
  return {
    close: useCloseWindow(),
    minimize: useMinimizeWindow(),
    maximize: useMaximizeWindow(),
    openWindow: useOpenWindow(),
    setTitle: useSetWindowTitle(),
    setParams: useSetWindowParams(),
  };
}
